/* ==========================================================================
   NWP HEADER — interaction enhancement layer
   Composes with Prestige 11.4.0 (theme.js custom elements + Motion One).

   - Menu open/close motion is upgraded through the theme's own seam
     (createShow/HideAnimationControls), preserving the .finished/.finish()
     contract that removes [open] and cancels stale close completions.
   - The gliding hover/focus pill is a single injected element positioned in
     .header coordinate space; it introduces no positioned wrappers, never
     toggles theme-owned state, and skips touch + reduced-motion contexts.
   - Idempotent: safe on initial load and on shopify:section:load re-renders.
   ========================================================================== */
import 'theme';
import { timeline, stagger } from 'vendor';

const EASE = [0.32, 0.72, 0, 1];
const IDENTITY = 'translateY(0) scale(1)';
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const finePointer = window.matchMedia('screen and (pointer: fine)');
const staggerOn = () => window.themeVariables?.settings?.staggerMenuApparition !== false;

/* CSS defaults to the native no-JavaScript fallback. Only reveal Prestige's
   drawer opener after its real custom element has registered successfully. */
void customElements.whenDefined('header-sidebar')
  .then(() => {
    document.documentElement.classList.add('nwp-header-sidebar-ready');
  })
  .catch(() => {});

/* :focus-visible in matches() throws on engines that lack the selector
   (e.g. Safari 15.0-15.3) — probe once, degrade to plain :focus. */
const focusVisibleSelector = (() => {
  try {
    document.documentElement.matches(':focus-visible');
    return ':focus-visible';
  } catch {
    return ':focus';
  }
})();

/* ---- 1. Menu animation upgrade ------------------------------------------ */

const popoutMotion = new WeakMap();
const UTILITY_POPOUT_SELECTOR = '.nwp-account__panel, cart-drawer.nwp-header-popout';

function finishPopoutMotion(panel) {
  const motion = popoutMotion.get(panel);
  if (!motion) return;
  motion.content.finish();
  motion.frames.forEach((animation) => animation.finish());
}

/* Account uses native details; Cart uses Prestige's dialog lifecycle. Retire
   the outgoing utility through its owner before the incoming one can paint.
   A sibling handoff replaces the panel without overlapping fades or motion. */
function handoffUtilityPopout(panel) {
  if (!panel.matches(UTILITY_POPOUT_SELECTOR)) return false;
  let handedOff = false;
  document.querySelectorAll(UTILITY_POPOUT_SELECTOR).forEach((other) => {
    if (other === panel) return;
    const details = other.closest('.nwp-account__disclosure');
    if (details?.open) {
      handedOff = true;
      stopPopoutMotion(other);
      details.removeAttribute('open');
      details.parentElement?.toggle?.(false);
    } else if (other.matches('cart-drawer[data-nwp-popout-visible]')) {
      handedOff = true;
      other.hide();
      finishPopoutMotion(other);
    }
  });
  return handedOff;
}

function stopPopoutMotion(panel) {
  const previous = popoutMotion.get(panel);
  if (!previous) return;
  popoutMotion.delete(panel);
  previous.content.stop();
  previous.frames.forEach((animation) => animation.cancel());
  panel.removeAttribute('data-nwp-popout-motion');
}

/* Keep the panel at opacity 1 with no animated transform: it must not
   become an intermediate backdrop root for the glass below its content. */
export function animatePopout(panel, opening) {
  const handedOff = opening && handoffUtilityPopout(panel);
  const previous = popoutMotion.get(panel);
  const continuing = previous?.pending === true;
  const duration = reduceMotion.matches ? 0.08 : opening ? 0.24 : 0.18;
  const frameFrom = continuing ? getComputedStyle(panel, '::before').opacity : opening ? '0' : '1';
  const scroll = panel.querySelector(':scope > .nwp-popout__scroll') ?? panel;
  const targets = panel.matches('cart-drawer.nwp-popout')
    ? ['header', 'body', 'footer'].map(part => panel.getShadowPartByName(part)).filter(Boolean)
    : Array.from(scroll.children).flatMap((child) => {
    if (child.tagName !== 'LI') return [child];
    return Array.from(child.querySelectorAll(':scope > a, :scope > dropdown-menu-disclosure > details > summary'));
  });
  const sequences = targets.map((target, index) => [
    target,
    {
      opacity: [continuing ? Number(getComputedStyle(target).opacity) : opening ? 0 : 1, opening ? 1 : 0],
      transform: [
        continuing ? getComputedStyle(target).transform : opening && !reduceMotion.matches ? 'translateY(4px)' : 'none',
        opening || reduceMotion.matches ? 'none' : 'translateY(-2px)',
      ],
    },
    {
      duration,
      at: 0,
      easing: EASE,
      delay: opening && staggerOn() && !continuing && !reduceMotion.matches ? Math.min(index * 0.018, 0.09) : 0,
    },
  ]);

  stopPopoutMotion(panel);
  panel.removeAttribute('data-nwp-popout-motion');
  void getComputedStyle(panel, '::before').animationName;
  panel.style.setProperty('--nwp-popout-frame-from', frameFrom);
  panel.style.setProperty('--nwp-popout-frame-duration', `${duration}s`);
  panel.setAttribute('data-nwp-popout-motion', opening ? 'enter' : 'leave');
  void getComputedStyle(panel, '::before').animationName;

  const frames = panel.getAnimations({ subtree: true }).filter((animation) =>
    animation.effect?.target === panel && animation.animationName?.startsWith('nwp-popout-frame-'));
  const content = timeline(sequences);
  const record = { frames, content, pending: true };
  popoutMotion.set(panel, record);
  const finished = Promise.all([content.finished, ...frames.map((animation) => animation.finished.catch(() => undefined))])
    .then((values) => {
      if (popoutMotion.get(panel) !== record) return undefined;
      record.pending = false;
      return values;
    });
  if (handedOff) finishPopoutMotion(panel);
  return {
    finished,
    finish() {
      content.finish();
      frames.forEach((animation) => animation.finish());
    },
  };
}

function patchMenuAnimations() {
  const dropdown = window.customElements.get('dropdown-menu-disclosure');
  const mega = window.customElements.get('mega-menu-disclosure');

  if (dropdown && !dropdown.prototype.__nwpPatched) {
    dropdown.prototype.__nwpPatched = true;

    dropdown.prototype.createShowAnimationControls = function () {
      preparePopout(this.contentElement);
      if (this.contentElement.matches(POPOUT_SELECTOR)) return animatePopout(this.contentElement, true);
      if (reduceMotion.matches) {
        return timeline([[this.contentElement, { opacity: [0, 1], transform: [IDENTITY, IDENTITY] }, { duration: 0.12 }]]);
      }
      const sequences = [
        [
          this.contentElement,
          { opacity: [0, 1], transform: ['translateY(-8px) scale(0.97)', 'translateY(0) scale(1)'] },
          { duration: 0.34, easing: EASE },
        ],
      ];
      if (staggerOn()) {
        const items = this.contentElement.querySelectorAll(':scope > li');
        if (items.length > 0) {
          sequences.push([
            items,
            { opacity: [0, 1], transform: ['translateY(10px)', 'translateY(0)'] },
            { duration: 0.3, at: '-0.26', easing: EASE, delay: stagger(0.028) },
          ]);
        }
      }
      return timeline(sequences);
    };

    dropdown.prototype.createHideAnimationControls = function () {
      if (this.contentElement.matches(POPOUT_SELECTOR)) return animatePopout(this.contentElement, false);
      if (reduceMotion.matches) {
        return timeline([[this.contentElement, { opacity: [1, 0], transform: [IDENTITY, IDENTITY] }, { duration: 0.1 }]]);
      }
      return timeline([
        [
          this.contentElement,
          { opacity: [1, 0], transform: ['translateY(0) scale(1)', 'translateY(-6px) scale(0.985)'] },
          { duration: 0.22, easing: EASE },
        ],
      ]);
    };
  }

  if (mega && !mega.prototype.__nwpPatched) {
    mega.prototype.__nwpPatched = true;

    mega.prototype.createShowAnimationControls = function () {
      preparePopout(this.contentElement);
      if (this.contentElement.matches(POPOUT_SELECTOR)) return animatePopout(this.contentElement, true);
      if (reduceMotion.matches) {
        return timeline([[this.contentElement, { opacity: [0, 1], transform: [IDENTITY, IDENTITY] }, { duration: 0.12 }]]);
      }
      const sequences = [
        [
          this.contentElement,
          { opacity: [0, 1], transform: ['translateY(-10px)', 'translateY(0)'] },
          { duration: 0.34, easing: EASE },
        ],
      ];
      if (staggerOn()) {
        const linklists = Array.from(this.contentElement.querySelectorAll('.mega-menu__linklist > li'));
        if (linklists.length > 0) {
          sequences.push({ name: 'content', at: '-0.24' });
          sequences.push([
            linklists,
            { opacity: [0, 1], transform: ['translateY(10px)', 'translateY(0)'] },
            { duration: 0.3, at: 'content', easing: EASE, delay: stagger(0.028) },
          ]);
        }
        const promo = this.contentElement.querySelector('.mega-menu__promo');
        if (promo) {
          sequences.push([promo, { opacity: [0, 1] }, { duration: 0.3, at: '-0.15' }]);
        }
      }
      return timeline(sequences);
    };

    mega.prototype.createHideAnimationControls = function () {
      if (this.contentElement.matches(POPOUT_SELECTOR)) return animatePopout(this.contentElement, false);
      if (reduceMotion.matches) {
        return timeline([[this.contentElement, { opacity: [1, 0], transform: [IDENTITY, IDENTITY] }, { duration: 0.1 }]]);
      }
      return timeline([
        [this.contentElement, { opacity: [1, 0], transform: ['translateY(0)', 'translateY(-6px)'] }, { duration: 0.22, easing: EASE }],
      ]);
    };
  }
}

/* ---- 2. Gliding hover/focus pill ---------------------------------------- */

const PAD_X = 14;
const PAD_Y = 7;

function initPill(header) {
  if (!finePointer.matches) return;
  if (!(window.CSS && CSS.supports('translate', '0px'))) return; // pill needs individual transform properties
  if (header.querySelector(':scope > .nwp-pill')) return;

  const nav = header.querySelector('.header__primary-nav');
  if (!nav) return;
  let lastInteractedSummary = null;

  const pill = document.createElement('span');
  pill.className = 'nwp-pill';
  pill.setAttribute('aria-hidden', 'true');
  /* Inline duplicates of the stylesheet's geometry: if nwp-header.css ever
     fails to load, the span stays a 0x0 absolutely-positioned no-op instead
     of occupying a phantom grid row. */
  pill.style.position = 'absolute';
  pill.style.left = '0';
  pill.style.top = '0';
  header.appendChild(pill);

  const targetOf = (node) =>
    node instanceof Element ? node.closest('.header__primary-nav-item :is(a, summary)[data-title]') : null;

  const hasVisibleBox = (element) => {
    if (!element?.isConnected) return false;
    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 && getComputedStyle(element).display !== 'none';
  };

  const isOpenSummary = (element) => {
    const details = element?.parentElement;
    return Boolean(
      details?.matches('details[open]')
        && hasVisibleBox(element),
    );
  };

  const rememberSummary = (element) => {
    const summary = element?.closest('summary[data-title]');
    if (summary && nav.contains(summary)) lastInteractedSummary = summary;
  };

  const openSummary = () => {
    if (isOpenSummary(lastInteractedSummary)) return lastInteractedSummary;
    const visibleOpenSummaries = Array.from(
      nav.querySelectorAll('details[open] > summary[data-title]'),
    ).filter(hasVisibleBox);
    return visibleOpenSummaries[visibleOpenSummaries.length - 1] ?? null;
  };

  const moveTo = (el) => {
    if (!hasVisibleBox(el)) return false;
    const headerRect = header.getBoundingClientRect();
    const rect = el.getBoundingClientRect();
    const neighbours = Array.from(nav.querySelectorAll('.header__primary-nav-item [data-title]'))
      .filter(other => other !== el && hasVisibleBox(other))
      .map(other => other.getBoundingClientRect());
    const spacing = neighbours.map(other => Math.max(other.left - rect.right, rect.left - other.right))
      .filter(gap => gap >= 0);
    const paddingX = Math.min(PAD_X, ...spacing.map(gap => gap / 2));
    pill.style.setProperty('--nwp-pill-x', (rect.left - headerRect.left - paddingX).toFixed(2));
    pill.style.setProperty('--nwp-pill-y', (rect.top - headerRect.top - PAD_Y).toFixed(2));
    pill.style.setProperty('--nwp-pill-w', (rect.width + paddingX * 2).toFixed(2));
    pill.style.setProperty('--nwp-pill-h', (rect.height + PAD_Y * 2).toFixed(2));
    return true;
  };

  const show = (el) => {
    if (!hasVisibleBox(el)) {
      hide();
      return;
    }
    const teleport = !pill.classList.contains('is-active') || reduceMotion.matches;
    if (teleport) {
      pill.classList.add('is-teleport');
      if (!moveTo(el)) return;
      void pill.offsetWidth; // flush so the position lands before transitions resume
      pill.classList.remove('is-teleport');
    } else {
      if (!moveTo(el)) return;
    }
    pill.classList.add('is-active');
  };

  const hide = () => pill.classList.remove('is-active');

  nav.addEventListener('pointerover', (event) => {
    const target = targetOf(event.target);
    if (target) {
      rememberSummary(target);
      show(target);
    }
  });

  nav.addEventListener('pointerleave', () => {
    const parked = openSummary();
    if (parked) show(parked);
    else hide();
  });

  nav.addEventListener('focusin', (event) => {
    const target = targetOf(event.target);
    if (target && target.matches(focusVisibleSelector)) {
      rememberSummary(target);
      show(target);
    }
  });

  nav.addEventListener('focusout', (event) => {
    if (event.relatedTarget instanceof Element && nav.contains(event.relatedTarget)) return;
    if (nav.matches(':hover')) return;
    const parked = openSummary();
    if (parked) show(parked);
    else hide();
  });

  /* Park the pill on the trigger of an open menu; release when it closes. */
  const observer = new MutationObserver(() => {
    const parked = openSummary();
    if (parked) {
      show(parked);
      return;
    }
    const active = document.activeElement;
    const focusedItem = active && nav.contains(active) && active.matches(focusVisibleSelector) ? targetOf(active) : null;
    if (focusedItem) {
      show(focusedItem);
    } else if (!nav.matches(':hover')) {
      hide();
    }
  });
  observer.observe(nav, {
    subtree: true,
    attributes: true,
    attributeFilter: ['open'],
  });

  const cleanup = new AbortController();
  window.addEventListener(
    'resize',
    () => {
      const parked = openSummary();
      if (parked) show(parked);
      else hide();
    },
    { passive: true, signal: cleanup.signal }
  );

  header.closest('.shopify-section')?.addEventListener(
    'shopify:section:unload',
    () => {
      cleanup.abort();
      observer.disconnect();
    },
    { once: true }
  );
}

/* ---- 3. Pop-out frame ----------------------------------------------------
   Every top-level header pop-out (mega menu, nav dropdown, account panel) is
   one notched block: the panel's own pseudo-elements paint its glass fill and
   hairline outline from a path computed here, with the notch tip under the
   trigger's centre (nwp-header.css §4b). No separate caret exists, so the
   notch enters, leaves and animates with the block. Drawn at Prestige's show
   seam before the enter animation, on resize while open, and whenever the
   panel's size changes (e.g. a validation message appears). */

const POPOUT_SELECTOR = '.nwp-popout';
const POPOUT_DROPDOWN_DETAILS = '.header__primary-nav-item > dropdown-menu-disclosure > details, .nwp-account__disclosure';
const POPOUT_NOTCH_FALLBACK_PX = 8.5;
const POPOUT_RADIUS_FALLBACK_PX = 12;
const popoutObservers = new WeakMap();
const popoutTriggers = new WeakMap();
const popoutHeaders = new WeakSet();

function tokenPx(value, fallback, emBase) {
  const token = String(value ?? '').trim();
  const number = Number.parseFloat(token);
  if (!Number.isFinite(number)) return fallback;
  if (token.endsWith('rem')) return number * Number.parseFloat(getComputedStyle(document.documentElement).fontSize);
  if (token.endsWith('em')) return number * emBase;
  return number;
}

/* Rounded rectangle whose top edge rises into a 45° notch at `apex`; `inset`
   shrinks the shape uniformly (0.5 keeps a 1px stroke crisp inside it). */
function popoutPath({ width, height, notch, radius, apex, inset = 0 }) {
  const f = (n) => String(Number(n.toFixed(2)));
  const left = inset;
  const right = width - inset;
  const top = notch + inset;
  const bottom = height - inset;
  const r = Math.max(0, Math.min(radius, (right - left) / 2, (bottom - top) / 2) - inset);
  const half = Math.max(0, notch - inset * (Math.SQRT2 - 1));
  const tipY = inset * Math.SQRT2;
  const ax = Math.min(Math.max(apex, left + r + half + 1), right - r - half - 1);
  return [
    `M${f(left + r)},${f(top)}`,
    `H${f(ax - half)}`,
    `L${f(ax)},${f(tipY)}`,
    `L${f(ax + half)},${f(top)}`,
    `H${f(right - r)}`,
    `A${f(r)},${f(r)} 0 0 1 ${f(right)},${f(top + r)}`,
    `V${f(bottom - r)}`,
    `A${f(r)},${f(r)} 0 0 1 ${f(right - r)},${f(bottom)}`,
    `H${f(left + r)}`,
    `A${f(r)},${f(r)} 0 0 1 ${f(left)},${f(bottom - r)}`,
    `V${f(top + r)}`,
    `A${f(r)},${f(r)} 0 0 1 ${f(left + r)},${f(top)}`,
    'Z',
  ].join(' ');
}

function positionPopout(panel) {
  const details = panel.closest('details');
  const header = panel.closest('.header');
  if (!details || !header || !details.matches(POPOUT_DROPDOWN_DETAILS)) return;
  const containingRect = (panel.offsetParent ?? details).getBoundingClientRect();
  const headerRect = header.getBoundingClientRect();
  const drop = Math.max(0, headerRect.bottom - containingRect.bottom);
  if (details.matches('.nwp-account__disclosure')) {
    // Position Account before the opening frame, through the same owner as
    // other popouts. A later native toggle event otherwise moves it mid-entry.
    details.style.setProperty('--nwp-account-drop', `${drop}px`);
    const summary = details.querySelector(':scope > summary');
    const anchor = summary?.querySelector('.nwp-account__anchor');
    if (anchor) {
      const style = getComputedStyle(header);
      const emBase = Number.parseFloat(style.fontSize) || 16;
      const gap = tokenPx(style.getPropertyValue('--nwp-popout-gap'), 12, emBase);
      const notch = tokenPx(style.getPropertyValue('--nwp-popout-notch'), POPOUT_NOTCH_FALLBACK_PX, emBase);
      anchor.style.setProperty('--nwp-account-bridge-height', `${Math.max(1, headerRect.bottom + gap - notch - summary.getBoundingClientRect().bottom)}px`);
    }
  } else {
    details.style.setProperty('--nwp-popout-drop', `${drop}px`);
  }
}

function drawPopout(panel, trigger) {
  popoutTriggers.set(panel, trigger);
  const width = panel.offsetWidth;
  const height = panel.offsetHeight;
  if (!width || !height) return;
  const style = getComputedStyle(panel);
  const emBase = Number.parseFloat(style.fontSize) || 16;
  const notch = tokenPx(style.getPropertyValue('--nwp-popout-notch'), POPOUT_NOTCH_FALLBACK_PX, emBase);
  const radius = tokenPx(style.getPropertyValue('--nwp-popout-radius'), POPOUT_RADIUS_FALLBACK_PX, emBase);
  const summary = trigger ?? panel.closest('details')?.querySelector(':scope > summary');
  const panelRect = panel.getBoundingClientRect();
  const summaryRect = summary?.getBoundingClientRect();
  const apex = summaryRect && summaryRect.width > 0
    ? summaryRect.left + summaryRect.width / 2 - panelRect.left
    : width / 2;
  const stroke = getComputedStyle(panel, '::after').color || 'currentColor';
  const clip = popoutPath({ width, height, notch, radius, apex });
  const outline = popoutPath({ width, height, notch, radius, apex, inset: 0.5 });
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}"><path d="${outline}" fill="none" stroke="${stroke}" stroke-width="1"/></svg>`;
  panel.style.setProperty('--nwp-popout-clip', `path("${clip}")`);
  panel.style.setProperty('--nwp-popout-outline', `url("data:image/svg+xml,${encodeURIComponent(svg)}")`);
  panel.setAttribute('data-nwp-popout-ready', '');
  if (!popoutObservers.has(panel) && typeof ResizeObserver === 'function') {
    const observer = new ResizeObserver(() => drawPopout(panel, popoutTriggers.get(panel)));
    observer.observe(panel);
    popoutObservers.set(panel, observer);
  }
}

export function preparePopout(panel, trigger) {
  if (!(panel instanceof Element) || !panel.matches(POPOUT_SELECTOR)) return;
  positionPopout(panel);
  drawPopout(panel, trigger);
}

function initPopouts(header) {
  if (popoutHeaders.has(header)) return;
  popoutHeaders.add(header);
  const cleanup = new AbortController();
  let frame = null;

  /* Nav dropdowns: measure the drop before Prestige opens on hover. */
  header.addEventListener(
    'mouseover',
    (event) => {
      const details = event.target instanceof Element ? event.target.closest(POPOUT_DROPDOWN_DETAILS) : null;
      const panel = details?.querySelector(`:scope > ${POPOUT_SELECTOR}`);
      if (panel && !details.open) positionPopout(panel);
    },
    { capture: true, signal: cleanup.signal },
  );

  window.addEventListener(
    'resize',
    () => {
      if (frame !== null) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        header.querySelectorAll(`details[open] > ${POPOUT_SELECTOR}`).forEach((panel) => {
          positionPopout(panel);
          drawPopout(panel);
        });
      });
    },
    { passive: true, signal: cleanup.signal },
  );

  header.closest('.shopify-section')?.addEventListener(
    'shopify:section:unload',
    () => {
      cleanup.abort();
      if (frame !== null) cancelAnimationFrame(frame);
      header.querySelectorAll(POPOUT_SELECTOR).forEach((panel) => {
        stopPopoutMotion(panel);
        // Release Prestige's document listeners without starting another fade.
        panel.closest('details')?.removeAttribute('open');
        const disclosure = panel.closest('dropdown-menu-disclosure, mega-menu-disclosure');
        if (typeof disclosure?.toggle === 'function') disclosure.toggle(false);
        popoutObservers.get(panel)?.disconnect();
        popoutObservers.delete(panel);
      });
      popoutHeaders.delete(header);
    },
    { once: true },
  );
}

/* ---- 4. Idempotent init -------------------------------------------------- */

function init(root) {
  patchMenuAnimations();
  const scope = root instanceof Element ? root : document;
  scope.querySelectorAll('x-header').forEach((header) => initPill(header));
  scope.querySelectorAll('x-header').forEach((header) => initPopouts(header));
}

init(document);
document.addEventListener('shopify:section:load', (event) => init(event.target));

export function releasePopout(panel) {
  stopPopoutMotion(panel);
  popoutObservers.get(panel)?.disconnect();
  popoutObservers.delete(panel);
}
